import { Amplify } from 'aws-amplify';
import { fetchAuthSession, resetPassword, signIn, signOut } from 'aws-amplify/auth';

let configured = false;
const AUTH_SESSION_TIMEOUT_MS = 5000;
const AUTH_SESSION_REFRESH_SKEW_MS = 60_000;

type CachedAuthSession = {
  accessToken: string;
  idToken: string;
  expiresAtMs: number;
};

type JwtTokenLike = {
  payload?: {
    exp?: number;
  };
  toString: () => string;
};

let cachedAuthSession: CachedAuthSession | null = null;
let pendingAuthSession: Promise<CachedAuthSession | null> | null = null;

const readConfig = () => {
  const userPoolId = process.env.REACT_APP_COGNITO_USER_POOL_ID;
  const userPoolClientId = process.env.REACT_APP_COGNITO_USER_POOL_CLIENT_ID;

  if (!userPoolId || !userPoolClientId) {
    return null;
  }

  return {
    Auth: {
      Cognito: {
        userPoolId,
        userPoolClientId,
      },
    },
  };
};

const ensureConfigured = (): boolean => {
  if (configured) {
    return true;
  }

  const config = readConfig();
  if (!config) {
    return false;
  }

  Amplify.configure(config);
  configured = true;
  return true;
};

const withTimeout = async <T>(promise: Promise<T>, timeoutMs: number): Promise<T> => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  try {
    return await Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        timeoutId = setTimeout(() => {
          reject(new Error('Auth request timed out'));
        }, timeoutMs);
      }),
    ]);
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
};

type AuthResult = {
  success: boolean;
  code?: string;
  message?: string;
};

const isInvalidCredentialsError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const maybeName = 'name' in error ? String((error as { name?: string }).name) : '';
  return maybeName === 'NotAuthorizedException' || maybeName === 'UserNotFoundException';
};

const clearAuthSessionCache = (): void => {
  cachedAuthSession = null;
  pendingAuthSession = null;
};

const getTokenExpiryMs = (token: JwtTokenLike | undefined): number | null => {
  const expiresAtSeconds = token?.payload?.exp;
  if (typeof expiresAtSeconds !== 'number' || !Number.isFinite(expiresAtSeconds)) {
    return null;
  }

  return expiresAtSeconds * 1000;
};

const isCachedSessionFresh = (session: CachedAuthSession | null): session is CachedAuthSession => {
  if (!session) {
    return false;
  }

  return Date.now() < session.expiresAtMs - AUTH_SESSION_REFRESH_SKEW_MS;
};

const cacheSessionFromAuthResponse = (session: Awaited<ReturnType<typeof fetchAuthSession>>): CachedAuthSession | null => {
  const accessToken = session.tokens?.accessToken as JwtTokenLike | undefined;
  const idToken = session.tokens?.idToken as JwtTokenLike | undefined;
  const accessTokenValue = accessToken?.toString();
  const idTokenValue = idToken?.toString();

  if (!accessTokenValue || !idTokenValue) {
    return null;
  }

  const expiresAtMs = getTokenExpiryMs(accessToken) ?? getTokenExpiryMs(idToken);
  if (!expiresAtMs) {
    return null;
  }

  return {
    accessToken: accessTokenValue,
    idToken: idTokenValue,
    expiresAtMs,
  };
};

const loadAuthSession = async (): Promise<CachedAuthSession | null> => {
  if (isCachedSessionFresh(cachedAuthSession)) {
    return cachedAuthSession;
  }

  if (pendingAuthSession) {
    return pendingAuthSession;
  }

  pendingAuthSession = (async () => {
    try {
      const session = await withTimeout(fetchAuthSession(), AUTH_SESSION_TIMEOUT_MS);
      const cachedSession = cacheSessionFromAuthResponse(session);
      if (cachedSession) {
        cachedAuthSession = cachedSession;
      } else {
        clearAuthSessionCache();
      }

      return cachedSession;
    } catch (error) {
      return isCachedSessionFresh(cachedAuthSession) ? cachedAuthSession : null;
    } finally {
      pendingAuthSession = null;
    }
  })();

  return pendingAuthSession;
};

export const amplifyAuthService = {
  async signInWithPassword(email: string, password: string): Promise<AuthResult> {
    if (!ensureConfigured()) {
      return {
        success: false,
        code: 'CONFIG_ERROR',
        message: 'Authentication is not configured.',
      };
    }

    clearAuthSessionCache();

    try {
      const result = await signIn({
        username: email,
        password,
        options: {
          authFlowType: 'USER_SRP_AUTH',
        },
      });

      if (result.isSignedIn) {
        return { success: true };
      }

      return {
        success: false,
        code: 'UNSUPPORTED_CHALLENGE',
        message: 'Additional auth challenge is not supported by this UI yet.',
      };
    } catch (error) {
      if (isInvalidCredentialsError(error)) {
        return {
          success: false,
          code: 'INVALID_CREDENTIALS',
          message: 'Invalid email or password.',
        };
      }

      return {
        success: false,
        code: 'AUTH_ERROR',
        message: 'Unable to sign in.',
      };
    }
  },

  async beginForgotPassword(email: string): Promise<AuthResult> {
    if (!ensureConfigured()) {
      return {
        success: false,
        code: 'CONFIG_ERROR',
        message: 'Authentication is not configured.',
      };
    }

    try {
      await resetPassword({ username: email });
      return {
        success: true,
      };
    } catch (error) {
      return {
        success: false,
        code: 'FORGOT_PASSWORD_FAILED',
        message: 'Unable to send reset instructions.',
      };
    }
  },

  async getSession(): Promise<{ authenticated: boolean }> {
    if (!ensureConfigured()) {
      return { authenticated: false };
    }

    const session = await loadAuthSession();
    return { authenticated: Boolean(session?.idToken) };
  },

  async getIdToken(): Promise<string | null> {
    if (!ensureConfigured()) {
      return null;
    }

    const session = await loadAuthSession();
    return session?.idToken ?? null;
  },

  async getAccessToken(): Promise<string | null> {
    if (!ensureConfigured()) {
      return null;
    }

    const session = await loadAuthSession();
    return session?.accessToken ?? null;
  },

  async signOut(): Promise<AuthResult> {
    if (!ensureConfigured()) {
      return { success: true };
    }

    try {
      await signOut();
      clearAuthSessionCache();
      return { success: true };
    } catch (error) {
      return {
        success: false,
        code: 'SIGN_OUT_ERROR',
        message: 'Unable to sign out.',
      };
    }
  },
};